// Personalization engine for skincare recommendations

// Skin type specific recommendations
export const skinTypeRecommendations = {
  dry: {
    focus: 'hydration and barrier repair',
    avoidIngredients: ['alcohol', 'harsh sulfates'],
    preferredIngredients: ['hyaluronic acid', 'ceramides', 'face oils'],
    tips: [
      'Use a gentle, cream-based cleanser',
      'Apply moisturizer while skin is still damp',
      'Consider a humidifier in your bedroom',
      'Avoid over-exfoliating'
    ],
    priority: ['moisturizing', 'protection', 'gentle treatments']
  },
  oily: {
    focus: 'oil control and pore management',
    avoidIngredients: ['heavy oils', 'comedogenic ingredients'],
    preferredIngredients: ['salicylic acid', 'niacinamide', 'clay'],
    tips: [
      'Use a foaming or gel cleanser',
      'Don\'t skip moisturizer - choose oil-free formulas',
      'Exfoliate regularly with BHA',
      'Use clay masks weekly'
    ],
    priority: ['cleansing', 'exfoliation', 'oil control']
  },
  combination: {
    focus: 'balanced care for different zones',
    avoidIngredients: ['one-size-fits-all products'],
    preferredIngredients: ['gentle acids', 'lightweight moisturizers'],
    tips: [
      'Use different products for T-zone and cheeks',
      'Gentle exfoliation 2x per week',
      'Lightweight, non-comedogenic moisturizer',
      'Spot treat oily areas with clay masks'
    ],
    priority: ['balanced cleansing', 'targeted treatments', 'protection']
  },
  sensitive: {
    focus: 'gentle care and irritation prevention',
    avoidIngredients: ['fragrances', 'essential oils', 'harsh acids'],
    preferredIngredients: ['ceramides', 'centella asiatica', 'zinc oxide'],
    tips: [
      'Patch test all new products',
      'Use fragrance-free, hypoallergenic products',
      'Gentle, minimal ingredient formulas',
      'Physical sunscreen over chemical'
    ],
    priority: ['gentle cleansing', 'soothing treatments', 'protection']
  },
  normal: {
    focus: 'maintenance and prevention',
    avoidIngredients: ['overly harsh treatments'],
    preferredIngredients: ['vitamin C', 'retinol', 'antioxidants'],
    tips: [
      'Maintain your routine consistently',
      'Introduce anti-aging ingredients gradually',
      'Regular exfoliation 1-2x per week',
      'Focus on prevention and protection'
    ],
    priority: ['maintenance', 'prevention', 'anti-aging']
  }
};

// Age-specific recommendations
export const ageRangeRecommendations = {
  teens: {
    focus: 'acne prevention and gentle care',
    concerns: ['acne', 'oiliness', 'hormonal changes'],
    keyIngredients: ['salicylic acid', 'benzoyl peroxide', 'niacinamide'],
    tips: [
      'Start with gentle products',
      'Don\'t over-treat acne',
      'Always use sunscreen',
      'Establish consistent routine'
    ],
    avoidIngredients: ['retinoids', 'strong acids']
  },
  '20s': {
    focus: 'prevention and routine establishment',
    concerns: ['occasional breakouts', 'sun damage prevention'],
    keyIngredients: ['vitamin C', 'hyaluronic acid', 'gentle exfoliants'],
    tips: [
      'Build a solid basic routine',
      'Start using antioxidants',
      'Never skip sunscreen',
      'Address specific concerns as they arise'
    ],
    avoidIngredients: ['overly aggressive treatments']
  },
  '30s': {
    focus: 'early anti-aging and maintenance',
    concerns: ['fine lines', 'uneven tone', 'loss of elasticity'],
    keyIngredients: ['retinol', 'peptides', 'vitamin C', 'AHA'],
    tips: [
      'Introduce retinol gradually',
      'Focus on prevention',
      'Regular professional treatments',
      'Eye cream becomes important'
    ],
    avoidIngredients: ['skipping anti-aging ingredients']
  },
  '40s': {
    focus: 'active anti-aging and repair',
    concerns: ['wrinkles', 'age spots', 'loss of firmness'],
    keyIngredients: ['retinoids', 'peptides', 'growth factors', 'AHA/BHA'],
    tips: [
      'Consistent retinoid use',
      'Layer hydrating products',
      'Consider professional treatments',
      'Neck and décolletage care'
    ],
    avoidIngredients: ['neglecting consistency']
  },
  '50plus': {
    focus: 'intensive repair and hydration',
    concerns: ['deep wrinkles', 'age spots', 'dryness', 'sagging'],
    keyIngredients: ['retinoids', 'peptides', 'ceramides', 'growth factors'],
    tips: [
      'Rich, nourishing formulas',
      'Multiple hydrating layers',
      'Regular professional treatments',
      'Full face, neck, and hand care'
    ],
    avoidIngredients: ['harsh, drying treatments']
  }
};

// Generate personalized welcome message
export const generateWelcomeMessage = (userProfile) => {
  const { name, skinType, ageRange, concerns } = userProfile;
  const skinRec = skinTypeRecommendations[skinType];
  const ageRec = ageRangeRecommendations[ageRange];

  let concernMessage = "";
  if (concerns && concerns.length > 0) {
    const primaryConcern = concerns.find(c => c.priority === 1);
    if (primaryConcern) {
      const concernNames = {
        'dryness': 'dryness',
        'oily-pores': 'oily skin and large pores',
        'acne': 'acne and breakouts',
        'aging': 'fine lines and aging',
        'dark-spots': 'dark spots',
        'dullness': 'dullness',
        'sensitivity': 'sensitivity and redness',
        'texture': 'uneven texture'
      };
      concernMessage = ` We'll start by focusing on your main concern: ${concernNames[primaryConcern.concern]}.`;
    }
  }

  return {
    greeting: `Welcome to your skincare journey, ${name}! ✨`,
    skinTypeMessage: `Your ${skinType} skin will benefit from our focus on ${skinRec.focus}.`,
    ageMessage: `At your age, we'll emphasize ${ageRec.focus}.${concernMessage}`,
    encouragement: "Let's build healthy habits that will give you glowing, confident skin!"
  };
};

// Get personalized tips for the day
export const getDailyTips = (userProfile, currentHabits) => {
  const { skinType, ageRange } = userProfile;
  const skinRec = skinTypeRecommendations[skinType];
  const ageRec = ageRangeRecommendations[ageRange];
  
  // Combine tips from skin type and age recommendations
  const allTips = [...skinRec.tips, ...ageRec.tips];
  
  // Return a random tip for variety
  const randomTip = allTips[Math.floor(Math.random() * allTips.length)];
  
  return {
    tip: randomTip,
    focus: skinRec.focus,
    keyIngredients: ageRec.keyIngredients.slice(0, 3) // Top 3 ingredients
  };
};

// Generate habit recommendations based on completion patterns
export const getHabitRecommendations = (userProfile, habitSchedules, personalizedHabits) => {
  const recommendations = [];
  const { skinType, ageRange } = userProfile;
  
  // Analyze completion patterns
  const habitStats = analyzeHabitCompletion(habitSchedules);
  
  // Find frequently missed habits
  const missedHabits = habitStats.filter(stat => stat.completionRate < 0.7);
  
  if (missedHabits.length > 0) {
    const mostMissed = missedHabits[0];
    const habit = personalizedHabits.find(h => h.id === mostMissed.habitId);
    
    if (habit) {
      recommendations.push({
        type: 'improvement',
        title: 'Boost Your Routine',
        message: `Try to be more consistent with "${habit.name}". This habit is key for ${skinTypeRecommendations[skinType].focus}.`,
        habitId: habit.id
      });
    }
  }
  
  // Suggest new habits based on age progression
  if (ageRange === '30s' && !personalizedHabits.find(h => h.id === 'retinol-treatment')) {
    recommendations.push({
      type: 'new-habit',
      title: 'Time for Anti-Aging',
      message: 'Consider adding retinol treatments to your routine for early anti-aging benefits.',
      habitId: 'retinol-treatment'
    });
  }
  
  return recommendations;
};

// Analyze habit completion patterns
const analyzeHabitCompletion = (habitSchedules) => {
  const stats = [];
  
  Object.keys(habitSchedules).forEach(habitId => {
    const schedule = habitSchedules[habitId];
    const completionHistory = schedule.completionHistory || {};
    const totalDays = Object.keys(completionHistory).length;
    const completedDays = Object.values(completionHistory).filter(Boolean).length;
    
    if (totalDays > 0) {
      stats.push({
        habitId,
        completionRate: completedDays / totalDays,
        totalDays,
        completedDays
      });
    }
  });
  
  return stats.sort((a, b) => a.completionRate - b.completionRate);
};

// Get motivational messages based on progress
export const getMotivationalMessage = (completionStreak, dayCompletion) => {
  if (completionStreak >= 7) {
    return {
      message: `Amazing! You're on a ${completionStreak}-day streak! 🔥`,
      type: 'celebration',
      emoji: '🎉'
    };
  } else if (completionStreak >= 3) {
    return {
      message: `Great momentum! ${completionStreak} days strong! 💪`,
      type: 'encouragement',
      emoji: '⭐'
    };
  } else if (dayCompletion === 100) {
    return {
      message: 'Perfect day! Your skin will thank you! ✨',
      type: 'daily-win',
      emoji: '🌟'
    };
  } else if (dayCompletion >= 75) {
    return {
      message: 'Almost there! Finish strong today! 💫',
      type: 'motivation',
      emoji: '🚀'
    };
  } else {
    return {
      message: 'Every step counts! Keep going! 🌱',
      type: 'gentle-push',
      emoji: '💚'
    };
  }
};
